import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Share2, BookOpen, Tag } from 'lucide-react';
import { articulos } from './articulos-data.jsx';
import SEOHead from '../../components/SEOHead';

const ArticuloDetalle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const articulo = articulos.find(a => a.slug === slug);

  if (!articulo) {
    return (
      <>
        <SEOHead 
          title="Artículo no encontrado"
          description="El artículo que buscas no existe. Explora nuestro blog con artículos sobre desarrollo web, diseño UX/UI y marketing digital."
          url={`/blog/${slug}`}
        />
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950/30 to-slate-950 pt-24 pb-16 flex items-center justify-center">
          <div className="text-center">
            <BookOpen className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-4">Artículo no encontrado</h2>
            <Link to="/blog" className="text-indigo-400 hover:text-indigo-300">
              ← Volver al blog
            </Link>
          </div>
        </div>
      </>
    );
  }

  const articulosRelacionados = articulos
    .filter(a => a.category === articulo.category && a.slug !== articulo.slug)
    .slice(0, 3);

  // Convertir fecha española a ISO (formato: "2 Dic 2024")
  const meses = {
    'Ene': '01', 'Feb': '02', 'Mar': '03', 'Abr': '04',
    'May': '05', 'Jun': '06', 'Jul': '07', 'Ago': '08',
    'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dic': '12'
  };
  
  let datePublished = '2024-12-01T00:00:00Z'; // Fecha por defecto
  try {
    const [dia, mes, año] = articulo.date.split(' ');
    const mesNumero = meses[mes] || '01';
    const diaFormateado = dia.padStart(2, '0');
    datePublished = `${año}-${mesNumero}-${diaFormateado}T00:00:00Z`;
  } catch (e) {
    console.warn('Error parseando fecha:', articulo.date);
  }

  // Structured data para el artículo
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": articulo.title,
    "description": articulo.excerpt,
    "image": "https://vapordesigns.netlify.app/images/LogoDefinitivo.png",
    "author": {
      "@type": "Organization",
      "name": articulo.author,
      "url": "https://vapordesigns.netlify.app"
    },
    "publisher": {
      "@type": "Organization", 
      "name": "VaporDesigns",
      "logo": {
        "@type": "ImageObject",
        "url": "https://vapordesigns.netlify.app/images/LogoDefinitivo.png"
      }
    },
    "datePublished": datePublished,
    "dateModified": new Date().toISOString(),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://vapordesigns.netlify.app/blog/${articulo.slug}`
    },
    "articleSection": articulo.category,
    "keywords": articulo.tags.join(', '),
    "wordCount": articulo.readTime.split(' ')[0] * 200,
    "timeRequired": articulo.readTime,
    "inLanguage": "es-ES",
    "isAccessibleForFree": true
  };

  return (
    <>
      <SEOHead 
        title={articulo.title}
        description={articulo.excerpt}
        keywords={articulo.tags.join(', ')}
        author={articulo.author}
        type="article"
        url={`/blog/${articulo.slug}`}
        publishedDate={datePublished}
        modifiedDate={new Date().toISOString()}
        tags={articulo.tags}
        category={articulo.category}
        readTime={articulo.readTime}
        structuredData={articleStructuredData}
      />
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950/30 to-slate-950 pt-24 pb-16 relative overflow-hidden">
      {/* Efectos de fondo */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-20 left-10 w-96 h-96 bg-indigo-500/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="relative z-10 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Botón volver */}
          <button
            onClick={() => navigate('/blog')}
            className="flex items-center gap-2 text-slate-300 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft size={20} />
            Volver al Blog
          </button>

          {/* Header del artículo */}
          <article className="bg-slate-900/50 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-slate-800 mb-8">
            {/* Imagen de portada */}
            {articulo.coverImage && (
              <div className="-mx-8 -mt-8 md:-mx-12 md:-mt-12 mb-8">
                {articulo.coverImage}
              </div>
            )}
            
            <div className="mb-6">
              <span className="px-4 py-2 bg-indigo-500/20 text-indigo-300 rounded-full text-sm font-semibold border border-indigo-500/30">
                {articulo.category}
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              {articulo.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-slate-400 mb-8 pb-8 border-b border-slate-800">
              <div className="flex items-center gap-2">
                <Calendar size={18} />
                <span>{articulo.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={18} />
                <span>{articulo.readTime}</span>
              </div>
              <div className="flex items-center gap-2">
                <span>Por {articulo.author}</span>
              </div>
              <button className="flex items-center gap-2 ml-auto text-indigo-400 hover:text-indigo-300 transition-colors">
                <Share2 size={18} />
                Compartir
              </button>
            </div>

            {/* Contenido del artículo */}
            <div className="prose prose-invert prose-lg max-w-none">
              {articulo.contenido}
            </div>

            {/* Tags */}
            <div className="mt-12 pt-8 border-t border-slate-800">
              <div className="flex items-center gap-2 flex-wrap">
                <Tag size={18} className="text-slate-400" />
                {articulo.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-slate-800/50 text-slate-300 rounded-lg text-sm border border-slate-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {/* Artículos relacionados */}
          {articulosRelacionados.length > 0 && (
            <div className="mt-16">
              <h2 className="text-3xl font-bold text-white mb-8">Artículos Relacionados</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {articulosRelacionados.map((art) => (
                  <Link
                    key={art.slug}
                    to={`/blog/${art.slug}`}
                    className="bg-slate-900/50 backdrop-blur-sm rounded-xl p-6 border border-slate-800 hover:border-indigo-500/50 transition-all group"
                  >
                    <div className="aspect-video bg-gradient-to-br from-indigo-900/50 to-purple-900/50 rounded-lg flex items-center justify-center mb-4">
                      <BookOpen className="w-12 h-12 text-indigo-400/50" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-indigo-400 transition-colors line-clamp-2">
                      {art.title}
                    </h3>
                    <p className="text-slate-400 text-sm line-clamp-2">
                      {art.excerpt}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-16 bg-gradient-to-br from-indigo-900/40 to-purple-900/40 backdrop-blur-xl rounded-3xl border border-indigo-500/30 p-12 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              ¿Necesitas ayuda con tu proyecto?
            </h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              En VaporDesigns transformamos ideas en soluciones digitales exitosas. Hablemos de tu proyecto.
            </p>
            <Link
              to="/#contacto"
              className="inline-block px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg shadow-indigo-500/30"
            >
              Contactar Ahora
            </Link>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ArticuloDetalle;
